import { useState } from 'react'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import type { Patient } from '../../../../model/patient'
import {
  getWhoData,
  axisConfig,
  type ChartType,
  type ChartSex,
  type PatientDataPoint
} from './data'
import ChartControls from './ChartControls'
import ChartDisplay from './ChartDisplay'
import messages from './messages'

interface GrowthChartProps {
  patient: Patient
  visits: any[]
}

function ageInMonths(dob: string, visitTimestamp: number): number | null {
  if (!dob) return null
  const birth = dayjs(dob, 'DD.MM.YYYY')
  if (!birth.isValid()) return null
  const months = dayjs(visitTimestamp).diff(birth, 'month', true)
  if (months < 0 || months > 60) return null
  return months
}

export function extractPatientPoints(
  visits: any[],
  chartType: ChartType,
  dob: string
): PatientDataPoint[] {
  const points: PatientDataPoint[] = []

  for (const visit of visits) {
    const months = ageInMonths(dob, visit.date ?? visit.createdAt)
    if (months === null) continue

    console.log(visit)

    let value: number | null = null

    if (chartType === 'weightForAge') {
      const w = parseFloat(visit.weight)
      if (!isNaN(w) && w > 0) value = w
    } else if (chartType === 'heightForAge') {
      const h = parseFloat(visit.height)
      if (!isNaN(h) && h > 0) value = h
    } else if (chartType === 'bmiForAge') {
      const w = parseFloat(visit.weight)
      const h = parseFloat(visit.height)
      if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
        value = Math.round((w / Math.pow(h / 100, 2)) * 10) / 10
      }
    } else if (chartType === 'headCircumferenceForAge') {
      const hc = parseFloat(visit.ofc)
      if (!isNaN(hc) && hc > 0) value = hc
    }

    if (value !== null) {
      points.push({
        month: Math.round(months * 10) / 10,
        value,
        visitDate: dayjs(visit.date).format('DD MMM YYYY')
      })
    }
  }

  return points.sort((a, b) => a.month - b.month)
}

export default function GrowthChart({ patient, visits }: GrowthChartProps) {
  const { formatMessage } = useIntl()
  const [chartType, setChartType] = useState<ChartType>('weightForAge')
  const [sex, setSex] = useState<ChartSex>(patient.sex ?? 'M')

  const percentiles = getWhoData(chartType, sex)
  const config = axisConfig[chartType]
  const patientPoints = extractPatientPoints(
    visits,
    chartType,
    patient.dateOfBirth
  )

  const measurementLabel = formatMessage(
    messages[chartType as keyof typeof messages] as Parameters<
      typeof formatMessage
    >[0]
  )

  return (
    <div>
      <ChartControls
        chartType={chartType}
        sex={sex}
        onChartTypeChange={setChartType}
        onSexChange={setSex}
      />
      <ChartDisplay
        percentiles={percentiles}
        patientPoints={patientPoints}
        yLabel={config.yLabel}
        yMin={config.yMin}
        yMax={config.yMax}
        measurementLabel={measurementLabel}
      />
    </div>
  )
}
